import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  IsActiveMatchOptions,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { Toast } from 'primeng/toast';
import { catchError, tap, throwError } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpinnerService } from '../../services/spinner.service';
import { BreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { FooterComponent } from '../footer/footer.component';
import { HeaderComponent } from '../header/header.component';
import { Spinner } from '../spinner/spinner';
import { APP_PATHS } from '../../config/app-paths';
import { ApplicationContextService } from '../../services/application-context.service';
import { AuthService } from '../../services/auth.service';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import { AdminMenuItem } from '../../../pages/admin-panel/admin-panel-wrapper/models/admin-menu-item';
import { DokumentApi } from '../../../shared/services/api/dokument.api';
import { DefinicjeParametrow } from '../../../shared/enums/params-definition.enum';
import { HttpResponseService } from '../../services/http-error-response.service';

@Component({
  selector: 'app-main-admin-layout',
  imports: [
    BreadcrumbComponent,
    FooterComponent,
    HeaderComponent,
    Toast,
    Spinner,
    AppIconComponent,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
  ],
  templateUrl: './main-admin-layout.component.html',
  styleUrl: './main-admin-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainAdminLayoutComponent {
  readonly #router = inject(Router);
  readonly #spinnerService = inject(SpinnerService);
  readonly #dokumentApi = inject(DokumentApi);
  readonly #httpResponseService = inject(HttpResponseService);
  protected closeSidebar = output<void>();
  manualURL = input<string>();
  public homePageUrl = input<string>();
  public manualUrl = input<string>();

  readonly #authService = inject(AuthService);
  protected readonly applicationContextService = inject(
    ApplicationContextService,
  );
  protected sidebarVisible = signal(false);
  protected isMobile = signal(false);
  public user = signal(this.#authService.getUser());

  protected readonly adminMenuItems: AdminMenuItem[] = [
    {
      id: 'home',
      label: 'Strona główna',
      icon: 'pi pi-home',
      route: `/${APP_PATHS.admin.root}`,
    },
    {
      id: 'management',
      label: 'Zarządzanie',
      icon: 'pi pi-cog',
      children: [
        {
          label: 'Wnioskami',
          route: `/${APP_PATHS.admin.root}/${APP_PATHS.admin.applications}`,
        },
        {
          label: 'Użytkownikami',
          route: `/${APP_PATHS.admin.root}/${APP_PATHS.admin.users}`,
        },
        {
          label: 'Komunikatami',
          route: `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}`,
        },
        {
          label: 'Regulaminem',
          route: `/${APP_PATHS.admin.root}/${APP_PATHS.admin.rules}`,
        },
      ],
    },
    {
      id: 'reports',
      label: 'Raporty',
      appIcon: 'report',
      route: `/${APP_PATHS.admin.root}/${APP_PATHS.admin.reports}`,
    },
    {
      id: 'admin-manual',
      label: 'Instrukcja administratora',
      icon: 'pi pi-book',
      action: () => this.openAdminManual(),
    },
  ];

  protected expandedGroups = signal<Record<string, boolean>>({
    management: true,
  });

  protected isGroupExpanded(item: AdminMenuItem): boolean {
    const key = item.id ?? item.label;

    return this.expandedGroups()[key] ?? true;
  }

  protected toggleGroup(item: AdminMenuItem): void {
    const key = item.id ?? item.label;

    this.expandedGroups.update((state) => ({
      ...state,
      [key]: !this.isGroupExpanded(item),
    }));
  }

  constructor() {
    this.#checkScreen();
    this.#subscribeRouterEventsListener();
  }

  @HostListener('window:resize')
  protected onResize(): void {
    this.#checkScreen();
  }

  protected toggleSidebar(): void {
    if (this.isMobile()) {
      this.sidebarVisible.update((value) => !value);
    }
  }

  protected isRouteActive(route?: string, exact = false): boolean {
    if (!route) {
      return false;
    }

    return this.#router.isActive(
      route,
      exact ? this.#exactMatchOptions : this.#subsetMatchOptions,
    );
  }

  protected currentAriaValue(route?: string): 'page' | 'location' | null {
    if (!route) {
      return null;
    }

    return this.#router.url === route ? 'page' : 'location';
  }

  protected openAdminManual(): void {
    this.#dokumentApi
      .getParamContent$(DefinicjeParametrow.ADRES_INSTRUKCJA_ADMINISTRATORA)
      .pipe(
        tap((response: string) => {
          if (response) {
            window.open(response, '_blank', 'noopener,noreferrer');
          }
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobrania instrukcji administratora',
          );
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  #checkScreen(): void {
    this.isMobile.set(window.innerWidth < 1024);
    if (!this.isMobile()) {
      this.sidebarVisible.set(true);
    } else {
      this.sidebarVisible.set(false);
    }
  }

  readonly #subsetMatchOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  readonly #exactMatchOptions: IsActiveMatchOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  };

  #subscribeRouterEventsListener(): void {
    this.#router.events
      .pipe(
        tap((event: any) => this.#navigationInterceptor(event as RouterEvent)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  #navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.#spinnerService.show();
    }

    if (event instanceof NavigationEnd) {
      this.#spinnerService.hide();
    }

    if (event instanceof NavigationCancel) {
      this.#spinnerService.hide();
    }
    if (event instanceof NavigationError) {
      this.#spinnerService.hide();
    }
  }
}
